import { StarIcon } from "@heroicons/react/24/solid";
import React, { FC } from "react";
import { formatDistance, parseISO, isValid } from "date-fns";
import { getChannelIcon } from "utils/getChannelIcon";
import { useClient } from "ClientContext";

export interface CommentListingProps {
  className?: string;
  hasListingTitle?: boolean;
  review?: {
    createdDateTime: string;
    expireAt: boolean | string;
    id: string;
    ratingScore: string;
    reviewText: string;
    channelId?: number;
  };
  [key: string]: any;
}

const CommentListing: FC<CommentListingProps> = ({
  className = "",
  hasListingTitle,
  review,
}) => {
  const { integration_TYPE } = useClient();
  const dateString = review?.createdDateTime || "";
  
  // If it's "Not available" or empty, treat as no valid date
  if (!dateString || dateString === "Not available") {
    return null; 
  }

  // Parse date
  const date = parseISO(dateString);

  // Double-check that we have a valid Date object
  if (!isValid(date)) {
    return null;
  }

  // Calculate the friendly date
  const friendlyDate = formatDistance(date, new Date(), { addSuffix: true });

  // if Hostaway / 2 is used, ratingScore is a string
  // Convert to number and divide by 2
  // to get the half rating
  // This is a workaround for the Hostaway API
  // which returns a rating score out of 10
  // but we want to display it out of 5
  
  // if Guesty / 5 is used, ratingScore is a number
  // Convert to number and divide by 5
  // to get the half rating
  // This is a workaround for the Guesty API
  // which returns a rating score out of 5    
  const halfRating = integration_TYPE ==="hostaway"? parseInt(review?.ratingScore || "0", 10) / 2 : parseInt(review?.ratingScore || "0", 10) ;


  return (
    <div
      className={`nc-CommentListing flex space-x-4 ${className}`}
      data-nc-id="CommentListing"
    >
      {/* Optional icon */}
      {getChannelIcon(review?.channelId||0)}

      <div className="flex-grow">
        <div className="flex justify-between items-start space-x-3">
          <div className="flex flex-col">
            <div className="text-sm font-semibold">
              {hasListingTitle && (
                <>
                  <span className="text-neutral-500 dark:text-neutral-400 font-normal">
                    {` review in `}
                  </span>
                </>
              )}
            </div>
            <span className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              {friendlyDate}
            </span>
          </div>

          {/* Stars Container */}
          <div className="flex justify-start text-yellow-500">
            {Array.from({ length: 5 }, (_, i) => (
              <StarIcon
                key={i}
                className={`w-4 h-4 ${i < halfRating ? "opacity-100" : "opacity-20"}`}
              />
            ))}
          </div>
        </div>

        <span className="block mt-3 text-neutral-600 dark:text-neutral-300">
          {review?.reviewText || ""}
        </span>
      </div>
    </div>
  );
};

export default CommentListing;